/**
 * Worked survey-research example + emitted DATA(Q) for each step type.
 * Shown in the per-step help popover next to the step's type label.
 *
 * Examples are concrete — a researcher should be able to recognise the
 * pattern from their own datasets (NPS, Top-2-Box, demographic banners,
 * country / brand sub-tables, age bands, awareness loops…). Keep them
 * realistic; abstract "Field A + Field B" patterns don't teach.
 */
import { TransformStepType } from "stores/queryBuilderStore"

export interface StepHelpExample {
  /** One- or two-sentence plain-English scenario in survey-research terms. */
  scenario: string
  /** Generated DATA(Q) for that scenario — mirrors what `generateStepScript`
   *  would emit. Multi-line. */
  script: string
}

export const STEP_TYPE_EXAMPLES: Record<TransformStepType, StepHelpExample> = {
  combine_or: {
    scenario:
      "Top-2-Box satisfaction. Combine the 'Very satisfied' and 'Satisfied' answers from 'Overall satisfaction' into one yes/no variable, so you can cross-tab Top-2-Box by region or segment.",
    script: `FIELD <<Top-2-Box satisfaction>> BOOL false
OR <<Top-2-Box satisfaction>> <<Overall satisfaction//Very satisfied>>
OR <<Top-2-Box satisfaction>> <<Overall satisfaction//Satisfied>>`,
  },
  and_combine: {
    scenario:
      "Qualified audience. A respondent counts only if they're both 'Aware of the brand' AND 'Considered the brand'. AND-joins the two answers into one boolean.",
    script: `FIELD <<Qualified audience>> BOOL
ASSIGN <<Qualified audience>> <<Aware of the brand//Yes>>
AND <<Qualified audience>> <<Considered the brand//Yes>>`,
  },
  negate: {
    scenario:
      "Non-promoters. 'Promoter' is true for scores 9–10; the inverse — Detractors + Passives — is everyone else. 'Invert in place' flips an existing yes/no; 'Create new' makes a sibling variable with the opposite values.",
    script: `FIELD <<Not a promoter>> BOOL
ASSIGN <<Not a promoter>> <<Net Promoter Score//Promoter>>
NOT <<Not a promoter>>`,
  },
  cleanup: {
    scenario:
      "After building a Top-2-Box NET, respondents who didn't see the satisfaction question (e.g. screened out earlier) appear as null. 'Fill blanks with false' converts them to 'No' so the NET reads as 0 instead of 'missing' in tables.",
    script: `BLANKFALSE <<Top-2-Box satisfaction>>`,
  },
  qtable: {
    scenario:
      "Create an empty banner group that you'll fill with demographic variables ('Age', 'Gender', 'Region') using the 'Append into container' step.",
    script: `QTABLE <<Demographics banner>>`,
  },
  field_decl: {
    scenario:
      "Seed a 'Survey complete' yes/no flag set to true for every respondent in the current run, then a numeric 'Sample weight' starting at 1, then a 'Bucket' field nested inside the existing 'Age' container.",
    script: `FIELD <<Survey complete>> BOOL true
FIELD <<Sample weight>> REAL 1
FIELD <<Age//Bucket>> TEXT`,
  },
  append: {
    scenario:
      "Assemble a demographics banner. Drop 'Age', 'Gender', and 'Region' under one parent group so you can run a single cross-tab with all three as columns.",
    script: `QTABLE <<Demographics banner>>
APPEND <<Demographics banner>> <<Age>>
APPEND <<Demographics banner>> <<Gender>>
APPEND <<Demographics banner>> <<Region>>`,
  },
  remove: {
    scenario:
      "Trim 'Don't know' and 'Prefer not to say' codes from a question before reporting, or drop a derived variable once you no longer need it.",
    script: `REMOVE <<Overall satisfaction//Don't know>>
REMOVE <<Overall satisfaction//Prefer not to say>>`,
  },
  conditional_assign: {
    scenario:
      "Country-specific eligibility. Flag a respondent as 'Eligible 18–34 in Australia' only when their country is Australia. Underlies subgroup metrics and weighting flags.",
    script: `FIELD <<Eligible 18-34 in Australia>> BOOL
__qcb_cond_xxxx BOOL false
OR __qcb_cond_xxxx <<Country//Australia>>
ASSIGN <<Eligible 18-34 in Australia>> <<Boolean//True>> IF:__qcb_cond_xxxx`,
  },
  copy_structure: {
    scenario:
      "Clone the structure of the 'Brand awareness' group into a new 'Brand awareness (filtered)' group so you can apply different blanking or filters without touching the original.",
    script: `QTABLE <<Brand awareness (filtered)>>
COPY <<Brand awareness (filtered)>> <<Brand awareness>>`,
  },
  loop: {
    scenario:
      "Iterate over every bank in 'Banks used'. Inside the loop, create per-bank sub-tables or assign per-bank flags. The loop variable (BANK) is substituted with each value at run time.",
    script: `FOR BANK <<Banks used>> - None
  QTABLE <<Products held by bank//BANK>>
ENDFOR`,
  },
  define: {
    scenario:
      "Give a long question name a short alias. Useful when the same prefix appears in many downstream steps — type 'AIDED' once instead of 'Brand awareness//Aided' everywhere.",
    script: `DEFINE AIDED Brand awareness//Aided`,
  },
  appendstruct: {
    scenario:
      "Inside a per-bank loop, copy the shape of a template ('Products held with bank - Bank 1') under a 'Products held by bank' container, aliased to the current bank. Builds per-bank sub-trees without re-declaring fields.",
    script: `APPENDSTRUCT <<Products held by bank>> <<Products held with bank - Bank 1>> "BANK"`,
  },
  calc: {
    scenario:
      "Total household children. Add three numeric counts ('Under 12', '12 to 17', '18 and over') into one INT field so you can band or filter on family size. Or build a yes/no flag like 'Prime working age' from an age comparison.",
    script: `FIELD <<Total children>> INT
CALC <<Total children>> "<<Children//Under 12>> + <<Children//12 to 17>> + <<Children//18 and over>>"`,
  },
  cat: {
    scenario:
      "Age bands. The 'Age band' container already holds yes/no fields '18-34', '35-54', '55+'. Drag it in — the categories auto-populate — then give each a range against the numeric 'Age' field. CAT sets each flag true for respondents whose age falls in its range.",
    script: `CAT <<Age band>> <<Age>> "18-34: 18-34; 35-54: 35-54; 55+: 55+"`,
  },
  weighting: {
    scenario:
      "Rim-weight the sample to population targets on several variables at once — Male 49% and Age 30/40/30 — so weighted tables match the population. Add each weighting variable, set its category targets, and the weights are raked across all of them and applied automatically.",
    script: `FIELD <<weights>> REAL
CALCWEIGHTS <<weights>> <<Gender//Male>> 49 <<Age//18-34>> 30 <<Age//35-54>> 40 <<Age//55+>> 30 AVERAGE 1
USEWEIGHTS <<weights>>`,
  },
}
